import { z } from "zod";

/**
 * FATURA PROFİLİ, ÖDEME YÖNTEMİ VE FATURA BELGELERİ (CLOS-202 `[176]`).
 *
 * ### ⚠ Ham kart alanı burada TANIMLANMADI ve bu bilerek
 *
 * KARAR 03: *"Closer ham kart numarası, CVV veya tam kart verisi saklamamalıdır."*
 * `addPaymentMethodSchema`'da PAN/CVV alanı YOK — şema `.strict()` olduğu için istemci
 * böyle bir alan gönderse bile 422 ile reddedilir. Kart girişi sağlayıcının kendi güvenli
 * yüzeyinde yapılır; Closer'a ulaşan tek şey sağlayıcının ürettiği token referansıdır.
 */

export const invoiceProfileSchema = z.object({
  id: z.uuid(),
  legalName: z.string(),
  taxIdOrNationalId: z.string(),
  addressLine1: z.string(),
  addressLine2: z.string().nullable(),
  city: z.string(),
  country: z.string(),
  postalCode: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  version: z.number().int(),
  updatedAt: z.string(),
});
export type InvoiceProfile = z.infer<typeof invoiceProfileSchema>;

export const upsertInvoiceProfileSchema = z.strictObject({
  legalName: z.string().trim().min(1, "Fatura unvanı zorunludur").max(200),
  taxIdOrNationalId: z
    .string()
    .trim()
    .min(1, "Vergi numarası veya T.C. kimlik numarası zorunludur")
    .max(32),
  addressLine1: z.string().trim().min(1, "Adres zorunludur").max(300),
  addressLine2: z.string().trim().max(300).optional(),
  city: z.string().trim().min(1, "Şehir zorunludur").max(100),
  country: z
    .string()
    .trim()
    .regex(/^[A-Z]{2}$/, "Ülke kodu ISO 3166-1 alpha-2 biçiminde olmalıdır (ör. TR)"),
  postalCode: z.string().trim().min(1, "Posta kodu zorunludur").max(20),
  email: z.email("Geçerli bir e-posta adresi giriniz"),
  phone: z.string().trim().max(30).optional(),
  /**
   * İYİMSER EŞZAMANLILIK: yeni profilde YOK (henüz sürüm yok); güncellemede ZORUNLU.
   * İki eşzamanlı düzenlemeden biri, gördüğü sürüm değişmişse `409` alır.
   */
  expectedVersion: z.number().int().positive().optional(),
});
export type UpsertInvoiceProfileInput = z.infer<typeof upsertInvoiceProfileSchema>;

export const paymentMethodSchema = z.object({
  id: z.uuid(),
  providerName: z.string(),
  brand: z.string(),
  last4: z.string(),
  expiryMonth: z.number().int(),
  expiryYear: z.number().int(),
  status: z.enum(["active", "removed"]),
  createdAt: z.string(),
});
export type PaymentMethod = z.infer<typeof paymentMethodSchema>;

/**
 * ⚠ `providerMethodRef` sağlayıcının hosted/tokenize akışının SONUCUDUR — istemci ham
 * kart göndermiyor, sağlayıcının döndürdüğü opak referansı gönderiyor. `brand`/`last4`/
 * `expiry*` de aynı akıştan gelen METADATA; kartın kendisi değil.
 */
export const addPaymentMethodSchema = z.strictObject({
  providerName: z.string().trim().min(1, "Sağlayıcı adı zorunludur").max(50),
  providerMethodRef: z.string().trim().min(1, "Sağlayıcı referansı zorunludur").max(200),
  brand: z.string().trim().min(1, "Kart markası zorunludur").max(30),
  last4: z
    .string()
    .trim()
    .regex(/^\d{4}$/, "Son dört hane 4 rakam olmalıdır"),
  expiryMonth: z.number().int().min(1, "Ay 1-12 arasında olmalıdır").max(12),
  expiryYear: z.number().int().min(2000, "Geçerli bir yıl giriniz").max(2100),
});
export type AddPaymentMethodInput = z.infer<typeof addPaymentMethodSchema>;

export const invoiceDocumentSchema = z.object({
  id: z.uuid(),
  type: z.enum(["invoice", "receipt", "credit_note"]),
  relatedDocumentId: z.uuid().nullable(),
  periodStart: z.string(),
  periodEnd: z.string(),
  amountMinorUnits: z.number().int(),
  currency: z.string(),
  taxAmountMinorUnits: z.number().int(),
  /** ⚠ Kolon DEĞİL, TÜRETİLMİŞ alan: bu belgeyi geçersiz kılan bir `credit_note` var mı? */
  voided: z.boolean(),
  createdAt: z.string(),
});
export type InvoiceDocument = z.infer<typeof invoiceDocumentSchema>;

export const documentDownloadTicketSchema = z.object({
  downloadUrl: z.string(),
  expiresInSeconds: z.number().int(),
});
export type DocumentDownloadTicket = z.infer<typeof documentDownloadTicketSchema>;
